import React, { useState, useContext, useEffect, useCallback } from 'react'
import PageWithTitleLayout from '../Layout/PageWithTitleLayout'
import { Tab,
        Box,
        Snackbar,
        Dialog,
        DialogTitle,
        DialogContent,
        DialogActions,
        Button,
        Alert
} from '@mui/material'
import { TabPanel,
    TabContext,
    TabList,
} from '@mui/lab'
import { isMobile } from '../../Utilities/isMobile'
import { useStickyString } from '../../Utilities/useStickyState'
import SubmittedEmployees from '../CreateShift/SubmittedEmployees'
import MonthlyShiftHeader from '../MonthlyShift/MonthlyShiftHeader'
import CheckShiftDay from '../CheckShift/CheckShiftDay'
import Calendar from '../SubmitShift/Calendar'
import CheckFinalizedCalendar from '../CheckShift/CheckFinalizedCalendar'
import HLGAdjustments from '../HLGAdjustments/HLGAdjustments'
import SalesAdjustments from '../SalesAdjustments/SalesAdjustments'
import { EmploymentsContext } from '../../contexts/EmploymentsContext'
import { useParams } from 'react-router-dom'
import { PATHS } from '../../constants'
import moment from 'moment'

const ShiftManagements = ({ history }) => {
    const { calendarYearMonth, tabValue: tabValueFromParam, employmentId } = useParams()
    const [tabValue, setTabValue] = useStickyString(tabValueFromParam, 'frederickTabValue')
    // Get selectedYearMonth from localStorage with fallback to current month if null
    const storedYearMonth = localStorage.getItem('frederickSelectedYearMonth')
    const selectedYearMonth = storedYearMonth ? JSON.parse(storedYearMonth) : moment().format('YYYY-MM')
    const [showMonthlyView, setShowMonthlyView] = useState(calendarYearMonth === selectedYearMonth)
    const [shiftDate, setShiftDate] = useState(!showMonthlyView ? calendarYearMonth : '')
    const { isManagerFor = {}, canEditShiftFor = {}, isAdmin, userInfo } = useContext(EmploymentsContext)
    const hasStoreManagerPermission = Boolean(isManagerFor[employmentId])
    const hasStoreShiftEditPermission = Boolean(canEditShiftFor[employmentId])
    const hasCreatePermission = hasStoreManagerPermission || isAdmin || hasStoreShiftEditPermission
    const hasShiftAdjustmentPermission = hasStoreManagerPermission || isAdmin
    const hasSalesAdjustmentPermission = hasStoreManagerPermission || isAdmin
    const hasSubmitPermission = !userInfo?.view_only
    const allowedTabs = ['check']
    if (hasCreatePermission) allowedTabs.push('status', 'create')
    if (hasSubmitPermission) allowedTabs.push('submit')
    if (hasShiftAdjustmentPermission) allowedTabs.push('adjust')
    if (hasSalesAdjustmentPermission) allowedTabs.push('salesAdjust')
    const effectiveTabValue = allowedTabs.includes(tabValue) ? tabValue : 'check'
    const [snackbar, setSnackbar] = useState({ open: false, message: '' })
    const notifySnackbar = useCallback(payload => {
        setSnackbar({ open: true, ...payload })
    }, [])
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [showNavigationPrompt, setShowNavigationPrompt] = useState(false)
    const [pendingTabNavigation, setPendingTabNavigation] = useState(null)
    const mobileViewOnly = isMobile() && userInfo.view_only
    const tabsRequiringMonthFormat = ['salesAdjust', 'adjust', 'status']

    useEffect(() => {
        if (shiftDate === '') {
            history.push(PATHS.shiftManagement.replace(':employmentId', userInfo?.view_only ? employmentId : localStorage.selected_emp).replace(':calendarYearMonth', selectedYearMonth).replace(':tabValue', tabValue))
            setShowMonthlyView(true)
        } else {
            history.push(history.location.pathname.replace(calendarYearMonth, shiftDate))
        }
    }, [shiftDate])

    useEffect(() => {
        const dateParts = calendarYearMonth.split('-')
        const isDateFormat = dateParts.length === 3 && moment(calendarYearMonth, 'YYYY-MM-DD', true).isValid()
        const isMonthlyFormat = dateParts.length === 2 && moment(calendarYearMonth, 'YYYY-MM', true).isValid()

        if (isMonthlyFormat) {
            setShowMonthlyView(true)
            setShiftDate('')
        } else if (isDateFormat) {
            setShowMonthlyView(false)
            setShiftDate(calendarYearMonth)
        } else if (calendarYearMonth !== shiftDate) {
            setShiftDate(calendarYearMonth)
        }
    }, [calendarYearMonth])

    useEffect(() => {
        if (tabValueFromParam && tabValueFromParam !== tabValue) {
            setTabValue(tabValueFromParam)
        }
    }, [tabValueFromParam, tabValue])

    const handleTabChange = (_, newValue) => {
        if (hasUnsavedChanges && tabValue === 'create') {
            // Show navigation prompt if there are unsaved changes and we're leaving the create tab
            setPendingTabNavigation(newValue)
            setShowNavigationPrompt(true)
        } else {
            let normalizedMonth = calendarYearMonth

            if (tabsRequiringMonthFormat.includes(newValue)) {
                normalizedMonth = moment(calendarYearMonth, ['YYYY-MM', 'YYYY-MM-DD'], true).format('YYYY-MM')
            }

            history.push(PATHS.shiftManagement
                .replace(':employmentId', employmentId)
                .replace(':calendarYearMonth', normalizedMonth)
                .replace(':tabValue', newValue))
            setTabValue(newValue)
        }
    }

    const handleNavigationConfirm = () => {
        setShowNavigationPrompt(false)
        if (pendingTabNavigation) {
            if (window.currentHistoryUnblock) {
                window.currentHistoryUnblock()
            }

            let normalizedMonth = calendarYearMonth

            if (tabsRequiringMonthFormat.includes(pendingTabNavigation)) {
                normalizedMonth = moment(calendarYearMonth, ['YYYY-MM', 'YYYY-MM-DD'], true).format('YYYY-MM')
            }

            history.push(PATHS.shiftManagement
                .replace(':employmentId', employmentId)
                .replace(':calendarYearMonth', normalizedMonth)
                .replace(':tabValue', pendingTabNavigation))
            setTabValue(pendingTabNavigation)
            setPendingTabNavigation(null)
        }
    }

    const handleNavigationCancel = () => {
        setShowNavigationPrompt(false)
        setPendingTabNavigation(null)
    }

    return (
        <PageWithTitleLayout
            title={mobileViewOnly ? '' : 'シフト管理'}
            titleOnly={userInfo.view_only}
            disableStoreSelect={effectiveTabValue === 'submit' && !hasCreatePermission}
        >
            <Snackbar
                open={snackbar.open}
                onClose={() => setSnackbar({ open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={4000}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
            <TabContext value={effectiveTabValue}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }} style={{ marginTop: mobileViewOnly ? '-7%' : '', backgroundColor: 'white'}}>
                    <TabList onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                        {hasCreatePermission && <Tab label="シフト提出状況" value={'status'} />}
                        {hasCreatePermission && <Tab label="シフト作成"  value={'create'} />}
                        <Tab label="シフト確認" value={'check'} />
                        {hasSubmitPermission && <Tab label="シフト提出" value={'submit'} data-tour="shift-submit-tab" />}
                        {hasShiftAdjustmentPermission && <Tab label="シフト調整" value={'adjust'} />}
                        {hasSalesAdjustmentPermission && <Tab label="売上調整" value={'salesAdjust'} />}
                    </TabList>
                </Box>
                <TabPanel value={'status'}>
                    {hasCreatePermission && <SubmittedEmployees history={history} setTabValue={setTabValue}/>}
                </TabPanel>
                <TabPanel value={'create'} sx={{p: 0}}>
                    {hasCreatePermission && (isMobile() ? <>
                        {showMonthlyView && <CheckFinalizedCalendar history={history} setShowMonthlyView={setShowMonthlyView} setShiftDate={setShiftDate}/>}
                        {!showMonthlyView && <CheckShiftDay history={history} setShowMonthlyView={setShowMonthlyView} shiftDate={shiftDate} setShiftDate={setShiftDate} setSnackbar={notifySnackbar} setHasUnsavedChanges={setHasUnsavedChanges}/>}
                    </> : <>
                        {showMonthlyView && <MonthlyShiftHeader history={history} setShowMonthlyView={setShowMonthlyView} setShiftDate={setShiftDate}/>}
                        {!showMonthlyView && <CheckShiftDay history={history} setShowMonthlyView={setShowMonthlyView} shiftDate={shiftDate} setShiftDate={setShiftDate} setSnackbar={notifySnackbar} setHasUnsavedChanges={setHasUnsavedChanges}/>}
                    </>)}
                </TabPanel>
                <TabPanel value={'check'} sx={{p: 0}}>
                    {isMobile() ? <>
                        {showMonthlyView && <CheckFinalizedCalendar history={history} setShowMonthlyView={setShowMonthlyView} setShiftDate={setShiftDate}/>}
                        {!showMonthlyView && <CheckShiftDay history={history} setShowMonthlyView={setShowMonthlyView} shiftDate={shiftDate} setShiftDate={setShiftDate} setSnackbar={notifySnackbar} setHasUnsavedChanges={setHasUnsavedChanges}/>}
                    </> : <>
                        {showMonthlyView && <MonthlyShiftHeader history={history} setShowMonthlyView={setShowMonthlyView} setShiftDate={setShiftDate}/>}
                        {!showMonthlyView && <CheckShiftDay history={history} setShowMonthlyView={setShowMonthlyView} shiftDate={shiftDate} setShiftDate={setShiftDate} setSnackbar={notifySnackbar} setHasUnsavedChanges={setHasUnsavedChanges}/>}
                    </>}
                </TabPanel>
                <TabPanel value={'submit'} sx={{p: 0}}>
                    <Box data-tour="shift-submit-calendar" sx={{ width: '100%' }}>
                        <Calendar history={history} />
                    </Box>
                </TabPanel>
                <TabPanel value={'adjust'}>
                    {hasShiftAdjustmentPermission && <HLGAdjustments />}
                </TabPanel>
                <TabPanel value={'salesAdjust'} sx={{p: 0}}>
                    {hasSalesAdjustmentPermission && <SalesAdjustments />}
                </TabPanel>
            </TabContext>

            {/* Navigation Prompt Dialog */}
            <Dialog
                open={showNavigationPrompt}
                onClose={handleNavigationCancel}
                aria-labelledby="navigation-prompt-title"
            >
                <DialogTitle id="navigation-prompt-title">
                    変更が保存されていません
                </DialogTitle>
                <DialogContent>
                    ページを切り替えると行った変更が保存されない可能性があります。よろしいですか。
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleNavigationCancel} color="primary">
                        キャンセル
                    </Button>
                    <Button onClick={handleNavigationConfirm} color="primary" autoFocus>
                        移動する
                    </Button>
                </DialogActions>
            </Dialog>
        </PageWithTitleLayout>

    )
}

export default ShiftManagements